/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import {
  type EChartsOption,
  type EChartsType,
  getInstanceByDom,
} from 'echarts';
import { useCallback } from 'react';

const useLegendVisibility = (
  chartRef: React.RefObject<HTMLDivElement | null>,
  selectedSeries: Record<string, boolean>,
) => {
  const setOnlyVisible = useCallback(
    (name: string | undefined) => {
      if (chartRef.current === null) {
        return;
      }
      const chart = getInstanceByDom(chartRef.current);

      chart?.dispatchAction({
        type: 'legendSelect',
        name: name,
      });

      const otherNames = Object.keys(selectedSeries).filter((n) => n !== name);
      otherNames.forEach((name) => {
        if (name.startsWith('thresholdLine')) {
          return;
        }

        chart?.dispatchAction({
          type: 'legendUnSelect',
          name: name,
        });
      });
    },
    [chartRef, selectedSeries],
  );

  const setAllVisible = useCallback(() => {
    if (chartRef.current === null) {
      return;
    }

    const chart = getInstanceByDom(chartRef.current);

    chart?.dispatchAction({
      type: 'legendAllSelect',
    });
  }, [chartRef]);

  /**
   * Toggle the visibility of a legend series
   *
   * The toggle logic depends on the current state of the series:
   * - If only the legend series that was selected is visible: show all series
   * - If all series are visible: hide all except the series that was selected
   * - Otherwise: toggle the legend series
   *
   * The isOnlyVisible and isAllSeriesSelected states can't be calculated in this
   * function since different legend types require different logic.
   */
  const toggleLegendVisibility = useCallback(
    (
      name: string | undefined,
      isAllSeriesSelected: boolean,
      isOnlyVisible: boolean,
    ) => {
      if (chartRef.current === null || name === undefined) {
        return;
      }

      if (isOnlyVisible) {
        setAllVisible();
      } else if (isAllSeriesSelected) {
        setOnlyVisible(name);
      } else {
        const chart = getInstanceByDom(chartRef.current);

        chart?.dispatchAction({
          name: name,
          type: 'legendToggleSelect',
        });
      }
    },
    [chartRef, setOnlyVisible, setAllVisible],
  );

  return { setAllVisible, setOnlyVisible, toggleLegendVisibility };
};

const isThresholdLine = (name?: string) => name?.startsWith('thresholdLine');

/**
 * Get the computed element width, including both paddings and margins
 */
const getComputedElementWidth = (element: Element) => {
  const elementRect = element.getBoundingClientRect();
  const elementComputedStyle = window.getComputedStyle(element);
  const elementMarginLeft = parseFloat(elementComputedStyle.marginLeft);
  const elementMarginRight = parseFloat(elementComputedStyle.marginRight);

  return elementRect.width + elementMarginLeft + elementMarginRight;
};

export const resetAllSeriesHighlight = (chart: EChartsType) => {
  const option = chart.getOption();
  const seriesList = (option.series as unknown[] | undefined) || [];

  seriesList.forEach((_, seriesIndex: number) => {
    chart.dispatchAction({
      seriesIndex,
      type: 'downplay',
    });
  });
};

const highlightOrDownplayLineAndBarSeries = (
  chart: EChartsType,
  currentSeriesToDispatch: { name: string; color: string }[],
  action: 'highlight' | 'downplay',
  currentChartOptions: ReturnType<EChartsType['getOption']>,
) => {
  const isDownplay = action === 'downplay';
  const allChartOptionSeries =
    (currentChartOptions?.series as Record<string, unknown>[] | undefined) ||
    [];

  // Change emphasis state to add opacity on the non highlighted items
  chart?.setOption({
    series: allChartOptionSeries?.map(
      (seriesItem: Record<string, unknown>) => ({
        ...seriesItem,
        emphasis: {
          ...(seriesItem.emphasis as Record<string, unknown>),
          disabled: isDownplay,
        },
      }),
    ),
  });

  const seriesWithSeriesIndex = allChartOptionSeries
    // Since the series index does not exist in the data we extract it from the series index
    ?.map((seriesItem, index) => ({
      ...seriesItem,
      seriesIndex: index,
    }))
    // Select the series that are in the currentSeriesToDispatch array
    ?.filter((seriesItem: Record<string, unknown>) =>
      currentSeriesToDispatch.some((series) => series.name === seriesItem.name),
    );

  if (!seriesWithSeriesIndex.length) {
    return;
  }

  chart?.dispatchAction({
    seriesIndex: seriesWithSeriesIndex.map((series) => series.seriesIndex),
    type: action,
  });
};

/**
 * Highlight or downplay pie chart dataset item
 */
const highlightOrDownplayPieSlices = (
  chart: EChartsType,
  currentSeriesToDispatch: { name: string; color: string }[],
  action: 'highlight' | 'downplay',
  currentChartOptions: ReturnType<EChartsType['getOption']>,
) => {
  const isDownplay = action === 'downplay';
  const allChartOptionSeries =
    (currentChartOptions?.series as Record<string, unknown>[] | undefined) ||
    [];

  // Change blur state to add opacity on the non highlighted items in the pie chart
  chart?.setOption({
    series: allChartOptionSeries?.map(
      (seriesItem: Record<string, unknown>) => ({
        ...seriesItem,
        blur: {
          itemStyle: {
            opacity: isDownplay ? 1 : 0.3,
          },
          label: {
            opacity: isDownplay ? 1 : 0.3,
          },
          // ...(seriesItem.blur as Record<string, unknown>),
        },
      }),
    ),
  });

  // The dataIndex that we want to dispatch can be found in the dataset[0] source array, where the first item is the name of the series
  const chartOptionDataset =
    currentChartOptions?.dataset as EChartsOption['dataset'];
  const datasetArray = Array.isArray(chartOptionDataset)
    ? chartOptionDataset
    : [chartOptionDataset];
  const sourceArray = datasetArray?.[0]?.source as unknown[][];

  // Find the dataIndexes that we want to dispatch
  const currentDataSeriesIndexes = currentSeriesToDispatch.map((series) => {
    const index = sourceArray?.findIndex((item) => item[0] === series.name) - 1;
    return index;
  });

  if (action === 'highlight') {
    // Downplay other data indexes to make sure the other slices are not highlighted
    const otherSeriesIndexes = sourceArray
      .map((_, index) => index)
      .filter((dataIndex) => !currentDataSeriesIndexes.includes(dataIndex));

    chart?.dispatchAction({
      seriesIndex: [0], // pie charts only has one series so seriesIndex is always 0
      dataIndex: otherSeriesIndexes,
      type: 'downplay',
    });
  }

  chart?.dispatchAction({
    seriesIndex: [0], // pie charts only has one series so otherSeriesIndexes is always 0
    dataIndex: currentDataSeriesIndexes,
    type: action,
  });
};

/**
 * Highlight or downplay the series in the chart to toggle between emphasized and non-emphasized state
 */
const highlightOrDownplaySeries = (
  chartRef: React.RefObject<HTMLDivElement | null>,
  series: { name: string; color: string }[],
  currentSelectedSeries: Record<string, boolean>,
  currentSeriesToDispatch: { name: string; color: string }[],
  action: 'highlight' | 'downplay',
) => {
  const hasOnlyOneSeries = series.length === 1;

  const isOnlyOneSeriesSelected =
    Object.values(currentSelectedSeries).filter(Boolean).length === 1;

  if (
    isOnlyOneSeriesSelected ||
    currentSeriesToDispatch.length === 0 ||
    chartRef.current === null ||
    hasOnlyOneSeries
  ) {
    return;
  }

  const chart = getInstanceByDom(chartRef.current);

  if (!chart) {
    return;
  }
  const currentChartOptions = chart.getOption();

  const seriesType = (
    currentChartOptions?.series as Record<string, unknown>[]
  )?.[0]?.type;

  if (seriesType === 'pie') {
    highlightOrDownplayPieSlices(
      chart,
      currentSeriesToDispatch,
      action,
      currentChartOptions,
    );
  } else {
    highlightOrDownplayLineAndBarSeries(
      chart,
      currentSeriesToDispatch,
      action,
      currentChartOptions,
    );
  }
};

export {
  useLegendVisibility,
  isThresholdLine,
  getComputedElementWidth,
  highlightOrDownplaySeries,
};
